import { Box, IconButton, Typography } from '@mui/material'
import i18n from 'i18n-js'
import { useEffect, useRef, useState } from 'react'
import BaseModal from '../../../../components/baseModal'
import Loader from '../../../../components/loader'
import { getObservationPhoto } from '../../../../services/observation'
import { Icons } from '../../../../utils'
import { styles } from './styles'

export const PhotoGalleryModal = ({ open, onClose, observationId, photos = [], observationText = '' }) => {
  const [slides, setSlides] = useState({})
  const [activeIndex, setActiveIndex] = useState(0)
  const slidesRef = useRef({})

  useEffect(() => {
    slidesRef.current = slides
  }, [slides])

  useEffect(() => {
    setActiveIndex(0)

    if (!open || !observationId || photos.length === 0) {
      return undefined
    }

    const initial = {}
    photos.forEach((photo) => {
      initial[photo.id] = { status: 'loading', url: null }
    })
    setSlides(initial)

    let cancelled = false

    photos.forEach(async (photo) => {
      const res = await getObservationPhoto(observationId, photo.id)
      if (cancelled) return

      setSlides((prev) => ({
        ...prev,
        [photo.id]: res?.data
          ? { status: 'ready', url: URL.createObjectURL(res.data) }
          : { status: 'error', url: null },
      }))
    })

    return () => {
      cancelled = true
    }
  }, [open, observationId])

  // revoke every blob url created by this modal instance on unmount
  useEffect(() => {
    return () => {
      Object.values(slidesRef.current).forEach((slide) => {
        if (slide.url) URL.revokeObjectURL(slide.url)
      })
    }
  }, [])

  const handleClose = () => {
    Object.values(slides).forEach((slide) => {
      if (slide.url) URL.revokeObjectURL(slide.url)
    })
    setSlides({})
    onClose()
  }

  const activePhoto = photos[activeIndex]
  const activeSlide = activePhoto ? slides[activePhoto.id] : null

  const goPrev = () => setActiveIndex((i) => (i - 1 + photos.length) % photos.length)
  const goNext = () => setActiveIndex((i) => (i + 1) % photos.length)

  if (!open) return null

  return (
    <BaseModal
      title={i18n.t('observation.photos.galleryTitle')}
      open={open}
      onClose={handleClose}
      modalProps={{ sx: styles.modal }}
      sxBody={styles.body}
    >
      <Box sx={styles.container}>
        {observationText && (
          <Typography variant="body2" sx={styles.observationText}>
            {observationText}
          </Typography>
        )}

        <Box sx={styles.viewer}>
          {photos.length > 1 && (
            <IconButton onClick={goPrev} sx={styles.navButton}>
              {Icons('left')}
            </IconButton>
          )}

          <Box sx={styles.imageBox}>
            {!activeSlide || activeSlide.status === 'loading' ? (
              <Loader size={32} removeMH />
            ) : activeSlide.status === 'error' ? (
              <Typography variant="body2" color="error">
                {i18n.t('general.dataNotFound')}
              </Typography>
            ) : (
              <Box component="img" src={activeSlide.url} sx={styles.image} alt="" />
            )}
          </Box>

          {photos.length > 1 && (
            <IconButton onClick={goNext} sx={styles.navButton}>
              {Icons('right')}
            </IconButton>
          )}
        </Box>

        {photos.length > 1 && (
          <Box sx={styles.thumbStrip}>
            {photos.map((photo, index) => {
              const slide = slides[photo.id]
              return (
                <Box
                  key={photo.id}
                  sx={[styles.thumb, index === activeIndex ? styles.thumbActive : null]}
                  onClick={() => setActiveIndex(index)}
                >
                  {slide?.url && (
                    <Box component="img" src={slide.url} sx={styles.thumbImg} alt="" />
                  )}
                </Box>
              )
            })}
          </Box>
        )}
      </Box>
    </BaseModal>
  )
}

export default PhotoGalleryModal

